import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowRight, Sparkles } from "lucide-react";
import Layout from "../components/Layout";
import { STEPS } from "../data/courseData";

export default function WeekCompleteInterstitial() {
  const { step } = useParams();
  const stepNum = parseInt(step, 10);
  const meta = STEPS.find((s) => s.step === stepNum);
  const next = STEPS.find((s) => s.step === stepNum + 1);
  const navigate = useNavigate();
  const accent = meta?.accent || "#22D3EE";
  const hasNext = !!next;

  return (
    <Layout hideNav>
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
        className="min-h-[80vh] flex flex-col items-center justify-center text-center"
      >
        <div className="relative mb-6">
          <motion.div
            className="absolute inset-0 rounded-full blur-2xl"
            style={{ background: `radial-gradient(circle, ${accent}88, transparent 70%)` }}
            animate={{ opacity: [0.55, 1, 0.55], scale: [1, 1.15, 1] }}
            transition={{ duration: 3.2, repeat: Infinity, ease: "easeInOut" }}
          />
          <div
            className="relative w-24 h-24 rounded-full flex items-center justify-center border"
            style={{
              borderColor: `${accent}66`,
              background: `radial-gradient(circle at 30% 30%, ${accent}33, ${accent}10 70%)`,
              boxShadow: `0 0 32px ${accent}55, inset 0 0 18px ${accent}22`,
            }}
          >
            <Check size={36} strokeWidth={2} style={{ color: accent }} />
          </div>

          <motion.div
            className="absolute inset-0"
            animate={{ rotate: 360 }}
            transition={{ duration: 18, repeat: Infinity, ease: "linear" }}
          >
            <Sparkles size={10} className="absolute -top-2 left-1/2 -translate-x-1/2" style={{ color: accent }} />
            <Sparkles size={8} className="absolute top-1/2 -right-2 -translate-y-1/2" style={{ color: "#F59E0B" }} />
            <Sparkles size={9} className="absolute -bottom-2 left-1/2 -translate-x-1/2" style={{ color: accent }} />
          </motion.div>
        </div>

        <div className="bps-eyebrow" style={{ color: accent }} data-testid="week-complete-eyebrow">
          Week {stepNum} complete
        </div>
        <h1 className="font-display text-3xl sm:text-4xl font-light tracking-tighter mt-3 text-white max-w-xs">
          You did all seven days.
        </h1>
        <p className="text-sm text-white/80 mt-5 leading-relaxed max-w-sm">
          That repetition is the rewire. Your nervous system noticed.
          {hasNext ? (
            <> <span className="text-white">Week {next.step} — {next.title}</span> is unlocked.</>
          ) : (
            <> You've moved through every layer of the program.</>
          )}
        </p>

        <div className="w-full max-w-sm mt-9 space-y-3">
          {hasNext ? (
            <button
              type="button"
              onClick={() => navigate(`/journey/${next.step}`)}
              data-testid="goto-next-week"
              className="w-full py-4 rounded-full font-semibold inline-flex items-center justify-center gap-2 hover:-translate-y-0.5 transition-all"
              style={{
                background: accent,
                color: "#03101F",
                boxShadow: `0 0 24px ${accent}66, inset 0 1px 0 rgba(255,255,255,0.25)`,
              }}
            >
              Begin Week {next.step} <ArrowRight size={16} strokeWidth={2.2} />
            </button>
          ) : (
            <button
              type="button"
              onClick={() => navigate("/graduation")}
              data-testid="goto-graduation"
              className="w-full py-4 rounded-full font-semibold inline-flex items-center justify-center gap-2 hover:-translate-y-0.5 transition-all"
              style={{
                background: "#F59E0B",
                color: "#03101F",
                boxShadow: "0 0 24px rgba(245,158,11,0.45), inset 0 1px 0 rgba(255,255,255,0.25)",
              }}
            >
              View your final report <ArrowRight size={16} strokeWidth={2.2} />
            </button>
          )}

          <button
            type="button"
            onClick={() => navigate("/home")}
            data-testid="return-home"
            className="w-full py-3 text-sm text-[#9AAEC6] hover:text-white transition-colors"
          >
            Return home
          </button>
        </div>
      </motion.div>
    </Layout>
  );
}
